import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "../components/Router";
import { isFollowed, updateFollowed } from "../components/Profile";
import { sortBy } from "../components/Sorting";
import { callLink } from "../components/Links.mjs";
import { dayAndTime } from "../components/Dates.mjs";
import { recordEvent } from "../components/Analytics";
import { useSessionContext } from "../components/Session";

import Button from "react-bootstrap/Button";
import playWhiteButton from "../img/play-white-button.svg";

import log from "loglevel";
import { isAfter } from "date-fns";
import { FaCalendarCheck, FaClock } from "react-icons/fa";
import { GrDocumentMissing } from "react-icons/gr";
import { BiBlock } from "react-icons/bi";
import SiteHead from "./SiteHead";
import Layout from "./Layout";
import HeroBgImage from "../img/hero-bg.png";
import { BarChartWidget } from "./BarChart";
import { CompanyLogoDiv } from "./Logo";
import noDataFound from "img/search-in-data.svg";
import "../sass/call-log.scss";

const EarningsEvent = ({
  exchange,
  symbol,
  isApproved,
  optOut,
  event: { year, quarter, conference_date, audio_file, has_conference_call },
}) => {
  // console.log("consol from table", year);
  const conferenceDate = new Date(conference_date);
  const today = new Date();
  const conferenceInFuture = isAfter(conferenceDate, today);
  const callPath = callLink(exchange, symbol, year, quarter);
  const { day, time } = dayAndTime(new Date(conference_date));
  log.debug(
    "EarningsEvent::render()",
    year,
    quarter.toUpperCase(),
    conference_date,
    isApproved,
    optOut
  );

  return (
    <>
      <tr>
        <td>
          <span>
            {/*<Badge variant="dark">*/}
            {quarter ? quarter.toUpperCase() : "Q?"}
            {/*</Badge>*/}
          </span>
        </td>
        <td>{year}</td>
        <td>{day}</td>
        <td>{time}</td>
        <td>
          <Link
            id={`call-${year + quarter.toUpperCase()}`}
            className="btn sound-lnk btn-success"
            to={callPath}
          >
            <span className="icon">
              {optOut ? (
                <>
                  <BiBlock />
                </>
              ) : (
                <>
                  {!has_conference_call && <GrDocumentMissing />}
                  {has_conference_call && (
                    <>
                      {audio_file ? (
                        <>
                          {isApproved ? (
                            <img src={playWhiteButton} alt="icon" />
                          ) : (
                            <FaClock />
                          )}
                        </>
                      ) : (
                        <>
                          {conferenceInFuture ? (
                            <FaCalendarCheck />
                          ) : (
                            <FaClock />
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </span>
            {optOut ? (
              <>Restricted</>
            ) : (
              <>
                {has_conference_call ? (
                  <>
                    {audio_file ? (
                      <span>{isApproved ? "Listen" : "Awaiting Approval"}</span>
                    ) : (
                      <span>
                        {conferenceInFuture ? "Scheduled" : "Pending"}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="no-call">No Conference Call</span>
                  </>
                )}
              </>
            )}
          </Link>
        </td>
      </tr>
    </>
  );
};

const CompanyFollowThingy = ({
  exchange,
  symbol,
  buildVersion,
  followed,
  setFollowed,
}) => {
  const { sessionContext } = useSessionContext();

  useEffect(() => {
    setFollowed(isFollowed(exchange, symbol));
  }, [setFollowed, exchange, symbol]);

  const onFollowClick = () => {
    log.info("Followed!", exchange, symbol);
    updateFollowed(exchange, symbol, !followed, sessionContext);
    setFollowed(!followed);
    recordEvent({
      name: followed ? "UnFollow" : "Follow",
      exchange,
      symbol,
      buildVersion,
    });
  };

  return (
    <Button variant="primary" onClick={onFollowClick}>
      {followed ? "Unfollow" : "Follow"}
    </Button>
  );
};

const getFollowerCount = (analytics) => {
  log.debug("Analytics", analytics);
  const {
    Play = 0,
    Pause = 0,
    Company = 0,
    Follow = 0,
    UnFollow = 0,
    SeekForward = 0,
  } = analytics || {};
  return Follow - UnFollow;
};

const computeFollowerCountIncludingSelf = (followedCount, followed) => {
  if (followed) {
    return followedCount + 1;
  }
  return followedCount;
};

const transformColsToRows = (data, variant) => {
  const rows = [];
  const { cols, x, y } = data;
  for (let i = 0; i < cols[0].length; i++) {
    const row = {};
    row[variant] = x[i];
    for (let j = 0; j < y.length; j++) {
      row[y[j]] = cols[j][i];
    }
    rows.push(row);
  }
  return {
    series: y,
    data: rows,
    xDataKey: variant,
  };
};

const transformV2DataResults = (v2) => {
  if (!v2) {
    return undefined;
  }
  const result = {};
  for (let [graphName, value] of Object.entries(v2)) {
    const { annual, quarterly } = value;
    result[graphName] = {
      annual: transformColsToRows(annual, "year"),
      quarter: transformColsToRows(quarterly, "quarter"),
    };
  }
  return result;
};

const Fundamentals = (props) => {
  const {
    pageContext: { fundamentals },
    toggleRecords,
  } = props;

  const [maxChartElements, setMaxChartElements] = useState(8);
  const { v2 } = fundamentals || {};
  const graphDataV2 = transformV2DataResults(v2);
  const { profit, shares } = graphDataV2 || {};
  const enabledFundamentalsFeature = true;
  const isDataPresent = !!v2;

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 575) {
        setMaxChartElements(8);
      } else {
        setMaxChartElements(4);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [toggleRecords]);

  if (!isDataPresent || !enabledFundamentalsFeature) {
    return (
      <>
        <div className="empty-block">
          <div className="img-container text-center">
            <img src={noDataFound} alt="No data found" />
          </div>

          <p className="text">No financial data available</p>
        </div>
      </>
    );
  }

  return (
    <>
      {/* TODO: Add Graph Title somewhere */}
      <BarChartWidget
        toggleRecords={toggleRecords}
        graphData={profit}
        maxChartElements={maxChartElements}
        dataType="currency"
      />
      <BarChartWidget
        toggleRecords={toggleRecords}
        graphData={shares}
        maxChartElements={maxChartElements}
      />
    </>
  );
};

const CompanyCalls = (props) => {
  const {
    pageContext: {
      exchange,
      symbol,
      earnings,
      analytics = {},
      fundamentals = {},
      company = {},
      buildVersion,
      isApproved,
      optOut,
    },
  } = props;

  const [followed, setFollowed] = useState(false);
  const [selectedBlockType, setSelectedBlockType] = useState("all");
  const [toggleRecords, setToggleRecords] = useState("annual");

  const followerCount = getFollowerCount(analytics);
  const followerCountIncludingSelf = computeFollowerCountIncludingSelf(
    followerCount,
    followed
  );

  const enabledFundamentalsFeature = true;
  const { v2 } = fundamentals || {};
  const isFinancialDataPresent = !!v2;

  log.trace(
    "Company::Render()",
    exchange,
    symbol,
    analytics,
    fundamentals,
    company
  );

  const {
    name,
    description,
    sector,
    industry, // Not currently used.  Should we display this?
    employees,
    address,
    website,
  } = company;

  const isCompanyDetailsPresent =
    !!description || !!sector || !!employees || !!address || !!website;

  useEffect(() => {
    recordEvent({ name: "Company", exchange, symbol, buildVersion });
  }, [exchange, symbol, buildVersion]);

  let earningsEvents = {};
  earnings.forEach((event) => {
    log.trace("Company:", event);
    const { year = "20??" } = event;
    if (!(year in earningsEvents)) {
      earningsEvents[year] = [];
    }
    if (!("quarter" in event)) {
      event.quarter = "Q?";
    }
    earningsEvents[year].push(event);
  });

  const orderedEvents = [];
  Object.keys(earningsEvents)
    .sort()
    .reverse()
    .forEach((year) => {
      earningsEvents[year]
        .sort(sortBy("quarter"))
        .reverse()
        .forEach((event) =>
          orderedEvents.push(
            <EarningsEvent
              key={`${year}-${event.quarter}`}
              exchange={exchange}
              symbol={symbol}
              event={event}
              isApproved={isApproved}
              optOut={optOut}
            />
          )
        );
    });

  let firstRow = [];
  orderedEvents.map((eachEvents) => {
    firstRow.push(eachEvents.props.event.year);
  });
  const uniqueItems = [...new Set(firstRow)];

  return (
    <>
      <SiteHead
        title={`EarningsCall · ${name}`}
        description={`Check out all the earnings calls for ${name}`}
        pageProps={props}
      />
      <Helmet>
        {/* Ensure the HeroBgImage is preloaded */}
        <link rel="preload" as="image" href={HeroBgImage} />
      </Helmet>

      <section className="profile-hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="company-info">
              <CompanyLogoDiv
                {...{ exchange, symbol, divClass: "img-container" }}
              />
              <div className="company-details">
                <h4 className="company-name">{name}</h4>
                <div className="detail-info">
                  <p className="text">
                    &nbsp;Call{earnings.length !== 1 && "s"}
                    <span className="highlight">{earnings.length}</span>
                  </p>
                  <p className="text">
                    &nbsp;Follower{followerCountIncludingSelf !== 1 && "s"}
                    <span className="highlight">
                      {" "}
                      {followerCountIncludingSelf}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            {/* The follow button */}
            <div className="button-container">
              <CompanyFollowThingy
                exchange={exchange}
                symbol={symbol}
                buildVersion={buildVersion}
                followed={followed}
                setFollowed={setFollowed}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="call-log-section">
        <div className="container">
          {/* Simple navbar for showing data */}

          <div className="document-list-container mx-auto">
            <ul className="document-list">
              <li
                className={
                  selectedBlockType === "all"
                    ? "document-list-item active"
                    : " document-list-item"
                }
                onClick={() => setSelectedBlockType("all")}
              >
                All Calls
              </li>
              {/* {enabledFundamentalsFeature && isFinancialDataPresent && ( */}
              {enabledFundamentalsFeature && (
                <>
                  <li
                    className={
                      selectedBlockType === "finances"
                        ? "document-list-item active"
                        : " document-list-item"
                    }
                    onClick={() => setSelectedBlockType("finances")}
                  >
                    Financials
                  </li>
                </>
              )}
              {isCompanyDetailsPresent && (
                <>
                  <li
                    className={
                      selectedBlockType === "details"
                        ? "document-list-item active"
                        : " document-list-item"
                    }
                    onClick={() => setSelectedBlockType("details")}
                  >
                    Company details
                  </li>
                </>
              )}
            </ul>
          </div>

          {selectedBlockType === "finances" && (
            <div className="bar-container">
              <div className="toggle-btn-container">
                <button
                  className={toggleRecords === "annual" ? "btn active" : "btn"}
                  onClick={() => setToggleRecords("annual")}
                  type="button"
                >
                  Annual
                </button>
                <button
                  className={toggleRecords === "quarter" ? "btn active" : "btn"}
                  onClick={() => setToggleRecords("quarter")}
                  type="button"
                >
                  Quarterly
                </button>
              </div>
              <div className="bar-block">
                <Fundamentals
                  {...props}
                  toggleRecords={toggleRecords}
                ></Fundamentals>
              </div>
            </div>
          )}

          {/* //Call Records Table Starts From Here */}

          {selectedBlockType === "all" && (
            <div className="tab-content" id="pills-tabContent">
              <div
                className="tab-pane fade show active"
                id="pills-recentCalls"
                role="tabpanel"
                aria-labelledby="pills-recentCalls-tab"
              >
                <div className="table-container">
                  <div className="table-responsive">
                    {uniqueItems.map((items) => (
                      <>
                        <p className="title-year">{items}</p>
                        <table>
                          <tbody>
                            {orderedEvents.filter(
                              (individualtable) =>
                                individualtable.props.event.year === items
                            )}
                          </tbody>
                        </table>
                      </>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Company Details Page for individuals */}
          {selectedBlockType === "details" && (
            <div className="company-details-container mx-auto">
              <div className="detail-block">
                {website && (
                  <p className="text">
                    Website:
                    <span className="text-link">
                      <a href={website}>{website}</a>
                    </span>
                  </p>
                )}
                {sector && (
                  <p className="text">
                    Sector:
                    <span className="text-link">{sector}</span>
                  </p>
                )}
                {employees && (
                  <p className="text">
                    Employees:
                    <span className="text-link">{employees}</span>
                  </p>
                )}
                {address && (
                  <p className="text">
                    Address:
                    <span className="text-link">{address}</span>
                  </p>
                )}
                {description && (
                  <div className="text-description">{description}</div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

const CompanyCallsExport = (props) => {
  const { pageContext } = props;
  const { exchange, symbol } = pageContext;
  const layoutProps = {
    ...props,
    analyticsOptions: {
      exchange,
      symbol,
    },
  };
  return (
    <Layout {...layoutProps}>
      <CompanyCalls {...props} />
    </Layout>
  );
};

export default CompanyCallsExport;
